import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Building2, MapPin, Plus, Edit2, Trash2, Loader2 } from "lucide-react";

export function DepartmentsManager() {
  const { currentWorkspace } = useWorkspace();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState({ name: "", locationId: "" });
  const [loading, setLoading] = useState(false);

  const wsId = currentWorkspace?._id;
  const isEnterprise = (currentWorkspace?.type as string) === "enterprise";

  const { data: departments = [] } = useQuery({
    queryKey: ["/api/departments"],
    queryFn: () => apiRequest("GET", "/api/departments"),
    enabled: !!wsId && isEnterprise,
  });

  const { data: locations = [] } = useQuery({
    queryKey: ["/api/inventory/locations"],
    queryFn: () => apiRequest("GET", "/api/inventory/locations"),
    enabled: !!wsId,
  });

  const { data: members = [] } = useQuery({
    queryKey: [`/api/workspaces/${wsId}/members`],
    queryFn: () => apiRequest("GET", `/api/workspaces/${wsId}/members`),
    enabled: !!wsId,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["/api/departments"] });

  const openCreate = () => { setEditing("new"); setForm({ name: "", locationId: "" }); };
  const openEdit = (dept: any) => { setEditing(dept); setForm({ name: dept.name, locationId: dept.locationId || "" }); };

  const save = async () => {
    if (!form.name.trim()) return toast({ title: "Name required", variant: "destructive" });
    setLoading(true);
    try {
      if (editing === "new") {
        await apiRequest("POST", "/api/departments", { name: form.name, locationId: form.locationId || null });
        toast({ title: "Department created" });
      } else {
        await apiRequest("PATCH", `/api/departments/${editing._id}`, { name: form.name, locationId: form.locationId || null });
        toast({ title: "Department updated" });
      }
      setEditing(null);
      refresh();
    } catch (e: any) {
      toast({ title: e.message || "Failed", variant: "destructive" });
    } finally { setLoading(false); }
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this department? Members will be unassigned.")) return;
    try {
      await apiRequest("DELETE", `/api/departments/${id}`);
      toast({ title: "Department deleted" });
      refresh();
    } catch (e: any) {
      toast({ title: e.message || "Failed", variant: "destructive" });
    }
  };

  if (!isEnterprise) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          <Building2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
          Departments are available for Enterprise workspaces only.
        </CardContent>
      </Card>
    );
  }

  const locationMap = Object.fromEntries((locations as any[]).map((l: any) => [l._id, l.name]));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Departments</h3>
        <Button size="sm" onClick={openCreate}><Plus className="h-4 w-4 mr-1" /> Add Department</Button>
      </div>

      {(departments as any[]).length === 0 ? (
        <Card><CardContent className="py-8 text-center text-muted-foreground">No departments yet</CardContent></Card>
      ) : (
        (departments as any[]).map((dept: any) => {
          const memberCount = (members as any[]).filter((m: any) => m.departmentId === dept._id).length;
          return (
            <Card key={dept._id}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{dept.name}</span>
                    <Badge variant="outline">{memberCount} member(s)</Badge>
                  </div>
                  {dept.locationId && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                      <MapPin className="h-3 w-3" /> {locationMap[dept.locationId] || "Unknown location"}
                    </div>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(dept)}><Edit2 className="h-3 w-3" /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => remove(dept._id)}><Trash2 className="h-3 w-3" /></Button>
                </div>
              </CardContent>
            </Card>
          );
        })
      )}

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{editing === "new" ? "New Department" : "Edit Department"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Name</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label>Location</Label>
              <Select value={form.locationId} onValueChange={v => setForm(f => ({ ...f, locationId: v }))}>
                <SelectTrigger><SelectValue placeholder="Select location" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="">None</SelectItem>
                  {(locations as any[]).map((l: any) => <SelectItem key={l._id} value={l._id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
